import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { AdminNav } from "@/components/AdminNav";
import { api } from "@/lib/api";
import {
  SECTIONS, STAGES, ADDED_TYPES, OVERRIDE_OPTION, buildSections, removedWith, sectionsForStage, isShown, whyId,
} from "@/lib/betaReport/schema";
import { builtInStrings, applyWording, LANGS } from "@/lib/betaReport/i18n";
import { editableKeys, textAt, loadBetaReportWording } from "@/lib/betaReport/wording";
import { Question } from "@/pages/BetaReport";

// Admin → Beta Report Wording (product owner, 2026-11-04). Edit the form in English:
// the wording, the choices, the hints inside comment boxes; delete questions and add
// new ones; change any question's answer type; reorder questions within a section.
// "Save and translate" makes the Portuguese, Spanish and French versions,
// which you can check and tweak; testers only see it after "Publish".

const LANG_LABEL = { en: "English", "pt-BR": "Português (Brasil)", "es-419": "Español", "fr-CA": "Français (Canada)" };
const ALL_KEYS = editableKeys();
const RATING_KEYS = ["excellent", "good", "fair", "poor"].map((k) => `ratings.${k}`);
const TYPE_LABEL = {
  rating: "Rating (Excellent / Good / Fair / Poor)", choice: "One choice", multi: "Several choices",
  yesno: "Yes / No", text: "Comment box", rating_na: "Rating (Excellent / Good / Fair / Poor) or N/A",
};
// Questions Admin → Beta Reports reads: deleting one stops that filter or column working.
const REPORT_USES = {
  a_overall: "the Overall column and the Overall rating filter",
  b_worked: "the Problems column and the Reported problems filter",
  b_problem_types: "the Problems column and the problem type filter",
  c_issue: "the Reported problems filter", d_mic_issue: "the Reported problems filter",
  h_crash: "the Reported problems filter", e_unfair: "the Reported problems filter",
  c_what_wrong: "the question issue filter",
};
const BUILT_IN = Object.fromEntries(SECTIONS.flatMap((s) => s.questions.map((q) => [q.id, { ...q, section: s.key }])));
const followUpsOf = (id) => Object.values(BUILT_IN).filter((q) => (q.showIf?.deps || []).includes(id));

const optionKeys = (q) => {
  if (q.type === "rating" || q.type === "rating_na") return RATING_KEYS.concat(q.type === "rating_na" ? ["ui.na"] : []);
  if (q.type === "yesno") return ["opt.yes", "opt.no"];
  if (q.type === "choice" || q.type === "multi") return q.options.map((o) => `opt.${o}`);
  if (q.type === "question_number") return ["ui.pickQuestion", "ui.noQuestion"];
  if (q.type === "language") return ["ui.languageAuto"];
  return [];
};

const GENERAL = [
  ["Top of the form", ["ui.eyebrow", "ui.title", "ui.intro", "ui.mode"]],
  ["Steps", STAGES.map((s) => `stages.${s}`)],
  ["Buttons and messages", ALL_KEYS.filter((k) => k.startsWith("ui.") && !["ui.eyebrow", "ui.title", "ui.intro", "ui.mode", "ui.na", "ui.pickQuestion", "ui.noQuestion", "ui.languageAuto"].includes(k))],
];

const newId = () => `x_${Array.from({ length: 6 }, () => "abcdefghijklmnopqrstuvwxyz0123456789"[Math.floor(Math.random() * 36)]).join("")}`;
const EMPTY = { deleted: [], added: [], overrides: {}, order: {} };
const norm = (s) => ({ deleted: s?.deleted || [], added: s?.added || [], overrides: s?.overrides || {}, order: s?.order || {} });
const OWN_OPTION_KEY = /^opt\.[a-z][a-z0-9_]*__o\d+$/;

export default function AdminBetaReportWording() {
  const [server, setServer] = useState(null);
  const [edits, setEdits] = useState({});
  const [struct, setStruct] = useState(EMPTY);
  const [lang, setLang] = useState("en");
  const [view, setView] = useState("edit");
  const [onlyChanged, setOnlyChanged] = useState(false);
  const [adding, setAdding] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(() => api.get("/admin/beta-report/wording").then(({ data }) => {
    setServer(data);
    setStruct(norm(data.structure));
  }).catch(() => toast.error("Couldn't load the Beta Report form.")), []);
  useEffect(() => { load(); }, [load]);

  const draft = useMemo(() => server?.draft || {}, [server]);
  const savedText = (l, key) => draft[l]?.[key] ?? textAt(builtInStrings(l), key) ?? "";
  const value = (l, key) => edits[l]?.[key] ?? savedText(l, key);
  const structDirty = JSON.stringify(struct) !== JSON.stringify(norm(server?.structure));
  const dirty = structDirty || Object.values(edits).some((m) => Object.keys(m).length > 0);
  const missing = useMemo(() => new Set((server?.missing || []).map((m) => `${m.lang}|${m.key}`)), [server]);
  const addedIds = useMemo(() => new Set(struct.added.map((a) => a.id)), [struct]);
  const ownOptions = useMemo(() => new Set(Object.values(struct.overrides).flatMap((o) => o.options || [])), [struct]);
  const isAddedKey = (key) => (/^(q|opt|ph)\.x_[a-z0-9]{6}/.test(key) && addedIds.has(key.split(".")[1].slice(0, 8)))
    || (OWN_OPTION_KEY.test(key) && ownOptions.has(key.slice(4)));
  const changedKeys = useMemo(() => new Set(Object.keys(draft.en || {}).concat(Object.keys(edits.en || {}))), [draft, edits]);
  const sections = useMemo(() => buildSections(struct), [struct]);
  const cascaded = useMemo(() => new Set(removedWith(struct)), [struct]);

  const setText = (l, key, text) => setEdits((prev) => {
    const mine = { ...(prev[l] || {}) };
    if (text === savedText(l, key)) delete mine[key];
    else mine[key] = text;
    return { ...prev, [l]: mine };
  });
  const resetKey = (key) => setText("en", key, textAt(builtInStrings("en"), key));
  const dropEdits = (prefixes) => setEdits((prev) => Object.fromEntries(Object.entries(prev).map(([l, m]) => [
    l, Object.fromEntries(Object.entries(m).filter(([k]) => !prefixes.some((p) => k.startsWith(p)))),
  ])));
  const updateAdded = (id, change) => setStruct((s) => ({ ...s, added: s.added.map((a) => (a.id === id ? { ...a, ...change } : a)) }));

  const deleteQuestion = (q) => {
    const title = value("en", `q.${q.id}`) || "this question";
    if (q.added) {
      if (!window.confirm(`Delete the question you added, "${title}"?`)) return;
      setStruct((s) => ({ ...s, added: s.added.filter((a) => a.id !== q.id) }));
      dropEdits([`q.${q.id}`, `opt.${q.id}_`, `ph.${q.id}`]);
      return;
    }
    const followUps = followUpsOf(q.id).filter((f) => !struct.deleted.includes(f.id));
    const lines = [`Delete "${title}"?`];
    if (followUps.length) lines.push(`Its ${followUps.length} follow-up question${followUps.length > 1 ? "s are" : " is"} deleted too.`);
    if (REPORT_USES[q.id]) lines.push(`Admin → Beta Reports uses this question for ${REPORT_USES[q.id]}; that stops working for new reports.`);
    lines.push("Reports already sent keep their answers. You can restore it before or after publishing.");
    if (!window.confirm(lines.join("\n\n"))) return;
    setStruct((s) => ({ ...s, deleted: [...new Set([...s.deleted, q.id])].sort() }));
  };
  const restore = (id) => setStruct((s) => ({ ...s, deleted: s.deleted.filter((d) => d !== id) }));

  // A built-in question's answers changed to another type (2026-11-04).
  const setAnswerType = (q, type) => {
    if (type === "original") {
      setStruct((s) => {
        const overrides = { ...s.overrides };
        delete overrides[q.id];
        return { ...s, overrides };
      });
      dropEdits([`opt.${q.id}__o`]);
      return;
    }
    if (!struct.overrides[q.id]) {
      const followUps = followUpsOf(q.id).filter((f) => !struct.deleted.includes(f.id));
      const lines = [];
      if (followUps.length) lines.push(`Its ${followUps.length} follow-up question${followUps.length > 1 ? "s depend" : " depends"} on the current answers and will be removed.`);
      if (REPORT_USES[q.id]) lines.push(`Admin → Beta Reports uses this question for ${REPORT_USES[q.id]}; that stops working for new reports.`);
      if (lines.length && !window.confirm(`Change the answers of "${value("en", `q.${q.id}`)}" to ${TYPE_LABEL[type]}?\n\n${lines.join("\n\n")}`)) return;
    }
    const options = type === "choice" || type === "multi"
      ? (struct.overrides[q.id]?.options?.length >= 2 ? struct.overrides[q.id].options : [OVERRIDE_OPTION(q.id, 1), OVERRIDE_OPTION(q.id, 2)])
      : [];
    if (!options.length) dropEdits([`opt.${q.id}__o`]);
    setStruct((s) => ({ ...s, overrides: { ...s.overrides, [q.id]: { type, options } } }));
  };
  const setOverrideOptions = (id, options) => setStruct((s) => ({ ...s, overrides: { ...s.overrides, [id]: { ...s.overrides[id], options } } }));

  // Move a question up or down within its section (2026-11-04).
  const move = (section, i, dir) => {
    const ids = section.questions.map((q) => q.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setStruct((s) => ({ ...s, order: { ...s.order, [section.key]: ids } }));
  };

  const addQuestion = () => {
    const id = newId();
    const options = adding.type === "choice" || adding.type === "multi" ? [`${id}_1`, `${id}_2`] : [];
    setStruct((s) => {
      const next = { ...s, added: [...s.added, { id, section: adding.section, after: adding.after, type: adding.type, required: adding.required, options }] };
      const order = s.order[adding.section];
      if (order?.length) {
        const list = order.filter((x) => x !== id);
        let at = list.length;
        if (adding.after === "__start") at = 0;
        else if (adding.after && list.includes(adding.after)) at = list.indexOf(adding.after) + 1;
        list.splice(at, 0, id);
        next.order = { ...s.order, [adding.section]: list };
      }
      return next;
    });
    setAdding(null);
    setOnlyChanged(false);
  };
  // Own choices: an added question's ("x_…_1") or a changed built-in's ("<id>__o1").
  const ownChoices = (q) => {
    if (q.added) {
      const a = struct.added.find((x) => x.id === q.id);
      return a && { options: a.options, make: (n) => `${a.id}_${n}`, update: (options) => updateAdded(a.id, { options }) };
    }
    const o = struct.overrides[q.id];
    return o && (o.type === "choice" || o.type === "multi")
      ? { options: o.options, make: (n) => OVERRIDE_OPTION(q.id, n), update: (options) => setOverrideOptions(q.id, options) } : null;
  };
  const addChoice = (own) => {
    const next = Math.max(0, ...own.options.map((o) => Number(o.split(/_|__o/).pop()))) + 1;
    if (next > 12 || own.options.length >= 12) return;
    own.update([...own.options, own.make(next)]);
  };
  const removeChoice = (own, opt) => {
    own.update(own.options.filter((o) => o !== opt));
    dropEdits([`opt.${opt}`]);
  };

  const save = async () => {
    const blank = struct.added.flatMap((a) => [`q.${a.id}`, ...a.options.map((o) => `opt.${o}`)])
      .concat(Object.values(struct.overrides).flatMap((o) => (o.options || []).map((x) => `opt.${x}`)))
      .filter((k) => !String(value("en", k)).trim());
    if (blank.length) {
      toast.error("A question still needs its question text or a choice text.");
      return;
    }
    const changes = Object.entries(edits.en || {}).map(([key, text]) => {
      const original = textAt(builtInStrings("en"), key) ?? "";
      const back = text.trim() === original;
      return {
        key, text: back ? null : text, before: savedText("en", key),
        before_translations: Object.fromEntries(LANGS.filter((l) => l !== "en").map((l) => [l, savedText(l, key)])),
      };
    });
    if (changes.some((c) => c.text !== null && !c.text.trim())) {
      toast.error("A text can't be empty. Use \"Original\" to go back to the built-in wording.");
      return;
    }
    const translations = Object.fromEntries(LANGS.filter((l) => l !== "en" && Object.keys(edits[l] || {}).length)
      .map((l) => [l, Object.fromEntries(Object.entries(edits[l]).filter(([k]) => !(edits.en || {})[k]))]));
    setBusy("save");
    try {
      const { data } = await api.put("/admin/beta-report/wording/draft", { changes, translations, structure: struct });
      setServer((prev) => ({ ...prev, ...data }));
      setStruct(norm(data.structure));
      setEdits({});
      toast.success(changes.length ? `Saved. ${data.translated} text(s) translated. Check them, then Publish.` : "Saved.");
      if (data.missing?.length) toast.error(`${data.missing.length} translation(s) failed. Type them in, or save again.`);
    } catch (ex) {
      const code = ex?.response?.data?.detail?.code;
      toast.error(code === "needs_two_choices" ? "A choice question needs at least two choices." : "Couldn't save. Nothing was changed.");
    } finally {
      setBusy("");
    }
  };

  const publish = async () => {
    setBusy("publish");
    try {
      await api.post("/admin/beta-report/wording/publish");
      await load();
      loadBetaReportWording({ force: true });
      toast.success("Published. Testers now see the new form in all four languages.");
    } catch (ex) {
      const code = ex?.response?.data?.detail?.code;
      toast.error(code === "missing_translations" ? "Some translations are missing (marked in red)."
        : code === "missing_english" ? "A question you added still needs its text." : "Couldn't publish.");
    } finally {
      setBusy("");
    }
  };

  const discard = async () => {
    if (!window.confirm("Discard the draft? The published form stays as it is.")) return;
    await api.delete("/admin/beta-report/wording/draft");
    setEdits({});
    load();
  };

  const field = (key, { multiline = false, label = "", placeholder = "" } = {}) => {
    if (onlyChanged && !changedKeys.has(key) && !isAddedKey(key)) return null;
    const isMissing = lang !== "en" && missing.has(`${lang}|${key}`) && !(edits[lang] || {})[key];
    const unsaved = (edits[lang] || {})[key] !== undefined;
    const changed = Boolean(draft.en?.[key]) || (lang === "en" && unsaved);
    const v = value(lang, key);
    const blankAdded = lang === "en" && isAddedKey(key) && !key.startsWith("ph.") && !v.trim();
    return (
      <div key={key} className={`rounded-lg p-2 ${isMissing || blankAdded ? "bg-rose-50 ring-1 ring-rose-300" : changed ? "bg-amber-50" : ""}`} data-testid={`wording-${key}`}>
        {label && <div className="text-[11px] text-[color:var(--text-secondary)] mb-1">{label}</div>}
        {multiline || v.length > 70 ? (
          <textarea value={v} onChange={(e) => setText(lang, key, e.target.value)} rows={Math.min(4, Math.ceil(v.length / 90) + 1)} maxLength={600}
            placeholder={placeholder} className="w-full rounded-md border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" />
        ) : (
          <input value={v} onChange={(e) => setText(lang, key, e.target.value)} maxLength={600} placeholder={placeholder}
            className="w-full rounded-md border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" />
        )}
        <div className="mt-1 flex flex-wrap gap-x-3 text-[11px] text-[color:var(--text-secondary)]">
          {lang !== "en" && changed && <span>English: {value("en", key)}</span>}
          {isMissing && <span className="text-rose-700">Needs a translation</span>}
          {blankAdded && <span className="text-rose-700">Needs text</span>}
          {unsaved && <span className="text-amber-700">Not saved yet</span>}
          {lang === "en" && draft.en?.[key] && !isAddedKey(key) && <button type="button" onClick={() => resetKey(key)} className="underline">Original</button>}
        </div>
      </div>
    );
  };

  const questionBlock = (q, i, section) => {
    const added = q.added ? struct.added.find((a) => a.id === q.id) : null;
    const own = ownChoices(q);
    const original = BUILT_IN[q.id];
    const canRetype = !q.added && original && !["language", "question_number"].includes(original.type);
    return (
      <div key={q.id} className={`border-l-2 pl-3 ${q.added || q.changedType ? "border-[color:var(--brand)]" : "border-[color:var(--border-stone)]"}`} data-testid={`wording-question-${q.id}`}>
        <div className="flex flex-wrap items-center gap-2 text-[11px] text-[color:var(--text-secondary)]">
          {lang === "en" && (
            <span className="inline-flex gap-1">
              <button type="button" onClick={() => move(section, i, -1)} disabled={i === 0} aria-label="Move up"
                className="rounded border border-[color:var(--border-stone)] px-1.5 disabled:opacity-30" data-testid={`wording-up-${q.id}`}>↑</button>
              <button type="button" onClick={() => move(section, i, 1)} disabled={i === section.questions.length - 1} aria-label="Move down"
                className="rounded border border-[color:var(--border-stone)] px-1.5 disabled:opacity-30" data-testid={`wording-down-${q.id}`}>↓</button>
            </span>
          )}
          {q.added && <span className="rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] px-2">Added · {TYPE_LABEL[q.type]}</span>}
          {canRetype && lang === "en" && (
            <label className="flex items-center gap-1">Answers
              <select value={struct.overrides[q.id]?.type || "original"} onChange={(e) => setAnswerType(q, e.target.value)}
                className="rounded border border-[color:var(--border-stone)] px-1 py-0.5" data-testid={`wording-type-${q.id}`}>
                <option value="original">Original: {TYPE_LABEL[original.type] || original.type}</option>
                {ADDED_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}{t === "choice" || t === "multi" ? " (own choices)" : ""}</option>)}
              </select>
            </label>
          )}
          {canRetype && lang !== "en" && q.changedType && <span className="rounded-full bg-[color:var(--brand)]/10 text-[color:var(--brand)] px-2">Answers: {TYPE_LABEL[q.type]}</span>}
          {added ? (
            <label className="flex items-center gap-1">
              <input type="checkbox" checked={added.required} onChange={(e) => updateAdded(q.id, { required: e.target.checked })} /> Required
            </label>
          ) : (
            <span>{q.required ? "Required" : "Optional"}{q.showIf ? " · only shown as a follow-up" : ""}</span>
          )}
          {lang === "en" && q.type !== "language" && (
            <button type="button" onClick={() => deleteQuestion(q)} className="ml-auto text-rose-700 underline" data-testid={`wording-delete-${q.id}`}>
              Delete question
            </button>
          )}
        </div>
        {q.type !== "question_number" && field(`q.${q.id}`, { placeholder: q.added ? "Type the question" : "" })}
        {(q.type === "rating" || q.type === "rating_na") && (
          <div className="text-[11px] text-[color:var(--text-secondary)] pl-2">Fair or Poor opens a required comment box (its wording is under Buttons and messages).</div>
        )}
        <div className="mt-1 grid grid-cols-1 sm:grid-cols-2 gap-1">
          {optionKeys(q).map((k) => (
            <div key={k} className="relative">
              {field(k, { placeholder: own ? "Type a choice" : "" })}
              {own && lang === "en" && own.options.length > 2 && (
                <button type="button" onClick={() => removeChoice(own, k.slice(4))} className="absolute right-3 top-3 text-xs text-rose-700" aria-label="Remove this choice">✕</button>
              )}
            </div>
          ))}
        </div>
        {own && lang === "en" && own.options.length < 12 && (
          <button type="button" onClick={() => addChoice(own)} className="mt-1 text-xs text-[color:var(--brand)] underline">+ Add a choice</button>
        )}
        {q.type === "text" && field(`ph.${q.id}`, { label: "Hint inside the box (optional; disappears when the tester types)", placeholder: lang === "en" ? "e.g. Describe what happened and on which screen" : "" })}
      </div>
    );
  };

  if (!server) return <div><AdminNav title="Beta Report Wording" /><p className="p-6 text-sm">Loading…</p></div>;

  return (
    <div className="min-h-screen bg-[color:var(--bg-base)]" data-testid="admin-beta-report-wording">
      <AdminNav title="Beta Report Wording" />
      <div className="sticky top-0 z-10 border-b border-[color:var(--border-stone)] bg-white/95 backdrop-blur px-6 py-3">
        <div className="max-w-5xl mx-auto flex flex-wrap items-center gap-3">
          <div className="inline-flex rounded-full border border-[color:var(--border-stone)] p-0.5" role="tablist">
            {["edit", "preview"].map((v) => (
              <button key={v} type="button" onClick={() => setView(v)} className={`rounded-full px-4 py-1.5 text-sm ${view === v ? "bg-[color:var(--brand)] text-white" : ""}`}>
                {v === "edit" ? "Edit" : "Preview as a tester"}
              </button>
            ))}
          </div>
          <select value={lang} onChange={(e) => setLang(e.target.value)} className="rounded-lg border border-[color:var(--border-stone)] bg-white px-2 py-1.5 text-sm" data-testid="wording-language">
            {LANGS.map((l) => <option key={l} value={l}>{LANG_LABEL[l]}{l === "en" ? " (edit here)" : ""}</option>)}
          </select>
          {view === "edit" && (
            <label className="text-sm flex items-center gap-1.5">
              <input type="checkbox" checked={onlyChanged} onChange={(e) => setOnlyChanged(e.target.checked)} /> Only changed
            </label>
          )}
          <div className="ml-auto flex items-center gap-2">
            <span className="text-xs text-[color:var(--text-secondary)]" data-testid="wording-status">
              {dirty ? "Unsaved changes" : server.has_draft ? `Draft, not published${server.missing?.length ? ` · ${server.missing.length} missing` : ""}` : "Matches what testers see"}
            </span>
            {server.has_draft && <button type="button" onClick={discard} className="text-sm underline text-[color:var(--text-secondary)]">Discard draft</button>}
            <button type="button" onClick={save} disabled={!dirty || !!busy} className="rounded-full border border-[color:var(--brand)] text-[color:var(--brand)] px-4 py-1.5 text-sm disabled:opacity-40" data-testid="wording-save">
              {busy === "save" ? "Translating…" : "Save and translate"}
            </button>
            <button type="button" onClick={publish} disabled={dirty || !server.has_draft || !!server.missing?.length || !!server.missing_english?.length || !!busy}
              className="rounded-full bg-[color:var(--brand)] text-white px-4 py-1.5 text-sm disabled:opacity-40" data-testid="wording-publish">
              {busy === "publish" ? "Publishing…" : "Publish"}
            </button>
          </div>
        </div>
      </div>

      <main className="max-w-5xl mx-auto px-6 py-6">
        {view === "edit" ? (
          <div className="space-y-6">
            <p className="text-sm text-[color:var(--text-secondary)] leading-relaxed">
              {lang === "en"
                ? "Edit in English: the questions, their choices and the hints inside comment boxes. Delete questions, add new ones, change any question's answers (\"Answers\"), and move questions with ↑ ↓ within their section. Then Save and translate: the Portuguese, Spanish and French versions are made for you; check them in the language list, tweak any you like, then Publish. Changed texts are highlighted; choices like Yes and No are shared, so a change shows wherever they're used."
                : `Translations into ${LANG_LABEL[lang]}. Texts changed in English show the English underneath. Edit any text here to correct it, then Save. Questions are added and deleted in English.`}
              {server.published_at && <> Last published {new Date(server.published_at).toLocaleString()}{server.published_by ? ` by ${server.published_by}` : ""}.</>}
            </p>
            {GENERAL.map(([title, keys]) => (
              <section key={title} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5">
                <h2 className="text-sm font-semibold uppercase tracking-widest text-[color:var(--text-secondary)] mb-3">{title}</h2>
                <div className="space-y-2">{keys.map((k) => field(k, { multiline: k === "ui.intro" }))}</div>
              </section>
            ))}
            {sections.map((section) => {
              const deletedHere = SECTIONS.find((s) => s.key === section.key).questions
                .filter((q) => struct.deleted.includes(q.id) || cascaded.has(q.id));
              return (
                <section key={section.key} className="rounded-2xl border border-[color:var(--border-stone)] bg-white p-5" data-testid={`wording-section-${section.key}`}>
                  <div className="text-xs text-[color:var(--text-secondary)] mb-1">Section {section.key} · step “{value(lang, `stages.${section.stage}`)}”</div>
                  {field(`sections.${section.key}`)}
                  <div className="mt-3 space-y-4">{section.questions.map((q, i) => questionBlock(q, i, section))}</div>
                  {deletedHere.length > 0 && !onlyChanged && (
                    <div className="mt-4 rounded-lg bg-[color:var(--bg-base)] p-3 text-xs space-y-1" data-testid={`wording-deleted-${section.key}`}>
                      <div className="font-medium text-[color:var(--text-secondary)]">Deleted from this section</div>
                      {deletedHere.map((q) => (
                        <div key={q.id} className="flex items-center gap-2 text-[color:var(--text-secondary)]">
                          <span className="line-through">{value("en", `q.${q.id}`) || q.id}</span>
                          {struct.deleted.includes(q.id)
                            ? lang === "en" && <button type="button" onClick={() => restore(q.id)} className="underline text-[color:var(--brand)]" data-testid={`wording-restore-${q.id}`}>Restore</button>
                            : <span>(a follow-up of a deleted question)</span>}
                        </div>
                      ))}
                    </div>
                  )}
                  {lang === "en" && (adding?.section === section.key ? (
                    <div className="mt-4 rounded-xl border border-[color:var(--brand)] p-3 text-sm space-y-2" data-testid={`wording-add-form-${section.key}`}>
                      <div className="font-medium">Add a question to this section</div>
                      <div className="flex flex-wrap gap-3">
                        <label className="flex items-center gap-1.5">Type
                          <select value={adding.type} onChange={(e) => setAdding({ ...adding, type: e.target.value })} className="rounded-md border border-[color:var(--border-stone)] px-2 py-1" data-testid="wording-add-type">
                            {ADDED_TYPES.map((t) => <option key={t} value={t}>{TYPE_LABEL[t]}</option>)}
                          </select>
                        </label>
                        <label className="flex items-center gap-1.5">Where
                          <select value={adding.after} onChange={(e) => setAdding({ ...adding, after: e.target.value })} className="rounded-md border border-[color:var(--border-stone)] px-2 py-1 max-w-xs">
                            <option value="">At the end</option>
                            <option value="__start">At the start</option>
                            {section.questions.map((q) => <option key={q.id} value={q.id}>After: {(value("en", `q.${q.id}`) || q.id).slice(0, 60)}</option>)}
                          </select>
                        </label>
                        <label className="flex items-center gap-1.5">
                          <input type="checkbox" checked={adding.required} onChange={(e) => setAdding({ ...adding, required: e.target.checked })} /> Required
                        </label>
                      </div>
                      <div className="flex gap-2">
                        <button type="button" onClick={addQuestion} className="rounded-full bg-[color:var(--brand)] text-white px-4 py-1.5" data-testid="wording-add-confirm">Add</button>
                        <button type="button" onClick={() => setAdding(null)} className="rounded-full border border-[color:var(--border-stone)] px-4 py-1.5">Cancel</button>
                      </div>
                      <div className="text-[11px] text-[color:var(--text-secondary)]">You'll type the question and its choices next. Added questions are always shown (no follow-up logic).</div>
                    </div>
                  ) : (
                    <button type="button" onClick={() => setAdding({ section: section.key, type: "rating", after: "", required: true })}
                      className="mt-4 text-sm text-[color:var(--brand)] underline" data-testid={`wording-add-${section.key}`}>
                      + Add question
                    </button>
                  ))}
                </section>
              );
            })}
          </div>
        ) : (
          <Preview lang={lang} sections={sections}
            strings={applyWording(builtInStrings(lang), { ...(draft[lang] || {}), ...(edits[lang] || {}) })} />
        )}
      </main>
    </div>
  );
}

// The form as a tester sees it, with the draft (and unsaved) changes. Nothing is saved.
function Preview({ lang, sections, strings: s }) {
  const [stage, setStage] = useState(0);
  const [answers, setAnswers] = useState({});
  const [mode, setMode] = useState("guided");
  const ctx = { language: lang, questionCount: 10 };
  const set = (id, v) => setAnswers((a) => ({ ...a, [id]: v }));
  return (
    <div className="rounded-2xl border border-dashed border-[color:var(--brand)] p-4 sm:p-6" data-testid="wording-preview">
      <p className="text-xs text-[color:var(--text-secondary)] mb-4">
        Preview only: answers aren't saved or sent. Follow-ups appear when you choose the answers that trigger them.
        {" "}Mode: {["guided", "live"].map((m) => (
          <button key={m} type="button" onClick={() => setMode(m)} className={`ml-1 underline ${mode === m ? "font-semibold" : ""}`}>{s.ui.modes[m]}</button>
        ))}
      </p>
      <div className="text-xs uppercase tracking-[0.2em] text-[color:var(--brand)]">{s.ui.eyebrow}</div>
      <h1 className="mt-2 text-2xl font-semibold tracking-tight">{s.ui.title}</h1>
      <p className="mt-3 text-sm text-[color:var(--text-secondary)] leading-relaxed">{s.ui.intro}</p>
      <div className="mt-4 inline-flex items-center gap-2 rounded-full border border-[color:var(--border-stone)] bg-white px-4 py-1.5 text-sm">
        <span className="text-[color:var(--text-secondary)]">{s.ui.mode}:</span><strong>{s.ui.modes[mode]}</strong>
      </div>
      <ol className="mt-6 flex flex-wrap gap-2">
        {STAGES.map((st, i) => (
          <li key={st}>
            <button type="button" onClick={() => setStage(i)} className={`rounded-full px-3 py-1 text-xs border ${i === stage ? "border-[color:var(--brand)] bg-[color:var(--brand)] text-white" : "border-[color:var(--border-stone)] text-[color:var(--text-secondary)]"}`}>{s.stages[st]}</button>
          </li>
        ))}
      </ol>
      <div className="mt-2 text-xs text-[color:var(--text-secondary)]">{s.ui.stage(stage + 1, STAGES.length)}</div>
      {sectionsForStage(STAGES[stage], sections).map((section) => (
        <section key={section.key} className="mt-6 rounded-2xl border border-[color:var(--border-stone)] bg-white p-5 space-y-5">
          <h2 className="text-lg font-semibold">{s.sections[section.key]}</h2>
          {section.questions.filter((q) => isShown(q, answers, ctx)).map((q) => (
            <Question key={q.id} q={q} s={s} value={answers[q.id]} lang={lang} questionCount={10} highlight={false}
              onChange={(v) => set(q.id, v)} why={answers[whyId(q.id)]} onWhy={(v) => set(whyId(q.id), v)} />
          ))}
        </section>
      ))}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {stage > 0 && <button type="button" onClick={() => setStage(stage - 1)} className="rounded-full border border-[color:var(--border-stone)] bg-white px-5 py-2.5 text-sm">{s.ui.back}</button>}
        {stage < STAGES.length - 1
          ? <button type="button" onClick={() => setStage(stage + 1)} className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium">{s.ui.next}</button>
          : <button type="button" disabled className="rounded-full bg-[color:var(--brand)] text-white px-6 py-2.5 text-sm font-medium opacity-60">{s.ui.submit}</button>}
        <span className="ml-auto text-sm text-[color:var(--text-secondary)] underline">{s.ui.later}</span>
      </div>
    </div>
  );
}
